import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { auth } from '../../services/api';
import SEOHead from '../common/SEOHead';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await auth.requestPasswordReset(email);
      setStatus('success');
      setMessage(response.message);
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Failed to send reset email');
    }
  };

  const forgotPasswordStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Forgot Password - altifi",
    "description": "Reset your altifi account password. Enter your email address and we'll send you a secure link to create a new password.",
    "url": "https://altifi.com/forgot-password",
    "isPartOf": {
      "@type": "WebSite",
      "name": "altifi",
      "url": "https://altifi.com"
    },
    "provider": {
      "@type": "Organization",
      "name": "altifi"
    }
  };

  return (
    <>
      <SEOHead 
        title="Forgot Password - altifi | Reset Your Account Password"
        description="Reset your altifi account password securely. Enter your email address and we'll send you a link to create a new password and regain access to your financial dashboard."
        keywords="forgot password, reset password, password recovery, altifi password, account recovery"
        canonicalUrl="https://altifi.com/forgot-password"
        noIndex={true}
        structuredData={forgotPasswordStructuredData}
      />
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography component="h1" variant="h5">
            Reset Password
          </Typography>

          <Typography color="text.secondary" align="center">
            Enter your email address and we'll send you a link to reset your password.
          </Typography>

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === 'loading'}
          />

          {status === 'success' && (
            <Alert severity="success" sx={{ width: '100%' }}>
              {message}
            </Alert>
          )}

          {status === 'error' && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {message}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={status === 'loading' || !email}
            sx={{ mt: 2 }}
          >
            {status === 'loading' ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Send Reset Link'
            )}
          </Button>

          <Box sx={{ mt: 2 }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Back to Login
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
    </>
  );
};

export default ForgotPassword; 